import React, { useEffect, useState, useCallback } from 'react';
import { BookOpen, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import SortableGrid from '../components/SortableGrid';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyBooks = () => {
  const { user, getAuthHeader } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadMyBooks = useCallback(async () => {
    try {
      const [booksRes, orderRes] = await Promise.all([
        axios.get(`${API_URL}/my-books`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/my-books/order`, { headers: getAuthHeader() })
      ]);

      const allBooks = booksRes.data;
      const savedOrder = orderRes.data.order || [];

      // Apply saved order
      if (savedOrder.length > 0) {
        const bookMap = new Map(allBooks.map(b => [b.id, b]));
        const ordered = [];
        for (const id of savedOrder) {
          if (bookMap.has(id)) {
            ordered.push(bookMap.get(id));
            bookMap.delete(id);
          }
        }
        // Append any books not in the saved order
        for (const b of bookMap.values()) ordered.push(b);
        setBooks(ordered);
      } else {
        setBooks(allBooks);
      }
    } catch (error) {
      toast.error('Failed to load your books');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (user) loadMyBooks();
  }, [user, loadMyBooks]);

  const handleReorder = useCallback(async (newOrder) => {
    setBooks(newOrder);
    try {
      await axios.put(`${API_URL}/my-books/reorder`, {
        order: newOrder.map(b => b.id)
      }, { headers: getAuthHeader() });
      toast.success('Library order saved');
    } catch {
      toast.error('Failed to save order');
    }
  }, [getAuthHeader]);

  const handleRead = (book) => navigate(`/read/${book.id}`);

  const handleOrderCopy = (book) => {
    addToCart(book);
    toast.success(`Added "${book.title}" to cart!`, {
      description: 'Order another copy for a friend or family member.'
    });
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Login Required</h2>
            <p className="text-gray-600 mb-6">Please log in to view your library.</p>
            <Button onClick={() => window.location.href = '/admin/login'}>Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your library...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">My Library</h1>
          <p className="text-gray-600">
            {books.length} {books.length === 1 ? 'book' : 'books'} in your collection
            {books.length > 1 && <span className="text-blue-600 ml-2 text-sm">— drag to reorder</span>}
          </p>
        </div>

        {books.length === 0 ? (
          <Card className="max-w-md mx-auto">
            <CardContent className="p-8 text-center">
              <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-800 mb-2">No books yet</h2>
              <p className="text-gray-600 mb-6">
                Start building your library by purchasing books from our collection.
              </p>
              <Button onClick={() => window.location.href = '/books'}>
                Browse Books
              </Button>
            </CardContent>
          </Card>
        ) : (
          <SortableGrid
            items={books}
            onReorder={handleReorder}
            enabled={books.length > 1}
            className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            renderItem={(book) => (
              <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300 group">
                <div className="relative overflow-hidden">
                  <img
                    src={book.coverImage}
                    alt={book.title}
                    loading="lazy"
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleRead(book)}
                        className="bg-blue-600 text-white hover:bg-blue-700"
                      >
                        <BookOpen className="h-4 w-4 mr-1" />
                        Read Now
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOrderCopy(book)}
                        className="bg-white/90 hover:bg-white border-white"
                      >
                        <ShoppingCart className="h-4 w-4 mr-1" />
                        Order Copy
                      </Button>
                    </div>
                  </div>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-bold text-gray-800 mb-1 line-clamp-2">{book.title}</h3>
                  <p className="text-sm text-gray-600 mb-2">{book.author}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{book.pages} pages</span>
                    <span className="text-xs text-blue-600 font-semibold">{book.ageRange}</span>
                  </div>
                </CardContent>
              </Card>
            )}
          />
        )}
      </div>
    </div>
  );
};

export default MyBooks;
